import { BrowserRouter, Route, Routes, Navigate, useLocation } from "react-router-dom";
import "./App.css";
import Header from "./Components/Header";
import Footer from "./Components/Footer";
import Step from "./Components/Step";
import ResumeQuote from './Components/ResumeQuote';
import QuotePage from "./Components/QuotePage";
import AgreementView from "./Components/AgreementView";
import CommercialAgreement from "./Components/CommercialAgreement";
import CommercialAgreementView from "./Components/CommercialAgreementView";
import ErrorBoundary from "./Components/ErrorBoundary";
import cabanaConfig from "./config/brand";
import { AdsKeys } from './Api/Constants';
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { StatsigProvider } from 'statsig-react';
import { STATSIG_CLIENT_KEY, getStatsigUser, getAndSeedSharedStableId } from './utils/statsig';
import BotDefense from './Components/BotDefense';
import AcquisitionPopup from './Components/AcquisitionPopup';
import { quoteEntryTarget } from './utils/quoteEntryTarget';
import { captureSplitQaParams } from './utils/splitTest';

function AppRoutes() {
  const location = useLocation();
  const isQuotePage = location.pathname.startsWith('/q/');
  const isAgreementView = location.pathname.startsWith('/agreement/');
  const isCommercialPage = location.pathname.startsWith('/commercial/') || location.pathname.startsWith('/commercial-agreement/');
  // Bot defense (Turnstile widget) only protects the lead form, so scope its
  // mount to /step/* routes. Keeps the fixed bottom-right widget from
  // overlapping mobile CTAs on quote/agreement pages.
  const isStepPage =
    location.pathname === '/' || location.pathname.startsWith('/step/');
  const hideChrome = isQuotePage || isAgreementView || isCommercialPage;

  return (
    <>
      {!hideChrome && <Header />}
      {isStepPage && <BotDefense />}
      <Routes>
        <Route path="/" element={<Navigate to={quoteEntryTarget(location.search)} replace />} />
        <Route path="/step/:index" element={
          <ErrorBoundary stage="step_render_error"><Step /></ErrorBoundary>
        } />
        <Route path="/q/:dealId" element={
          <ErrorBoundary stage="quote_page_render_error"><QuotePage config={cabanaConfig} /></ErrorBoundary>
        } />
        <Route path="/agreement/:dealId" element={
          <ErrorBoundary stage="agreement_view_render_error"><AgreementView config={cabanaConfig} /></ErrorBoundary>
        } />
        <Route path="/commercial/:dealId" element={
          <ErrorBoundary stage="commercial_agreement_render_error"><CommercialAgreement config={cabanaConfig} /></ErrorBoundary>
        } />
        <Route path="/commercial-agreement/:dealId" element={
          <ErrorBoundary stage="commercial_agreement_view_render_error"><CommercialAgreementView config={cabanaConfig} /></ErrorBoundary>
        } />
      </Routes>
      {!hideChrome && <Footer />}
    </>
  );
}

function App() {
  // Recovery URLs carry a bearer token in their fragment. Keep this screen
  // outside acquisition SDKs and session recording, and strip it on mount.
  if (window.location.pathname === '/r') return <ResumeQuote />;
  // Tokenized agreements do not need acquisition SDKs or session recording.
  if (/^\/commercial(?:-agreement)?\//.test(window.location.pathname)) {
    return <BrowserRouter><AppRoutes /></BrowserRouter>;
  }

  // Split-test QA override: read and strip ?splitqa=&splitarm= before any
  // first-touch capture below can record the token as the landing page.
  captureSplitQaParams();

  const searchParams = new URLSearchParams(window.location.search)

  // Scrub junk tracking values left in persistent storage by pre-consolidation
  // funnel builds, which stored String(null) for every ad key absent from a
  // utm_source-tagged URL (GMB / chatgpt arrivals). Returning visitors still
  // carry them and they end up as custom_gclid="null" on Sell deals, which
  // Google rejects (UNPARSEABLE_GCLID) and which blocks later attribution
  // backfill. Runs before capture so a real value from this visit can land.
  const junkTrackingValues = new Set(['null', 'undefined', 'none', 'nan', 'false']);
  const isJunkTracking = (v) => !v || junkTrackingValues.has(String(v).trim().toLowerCase());
  AdsKeys.forEach(item => {
    try {
      if (localStorage.getItem(item) !== null && isJunkTracking(localStorage.getItem(item))) {
        localStorage.removeItem(item);
      }
      if (sessionStorage.getItem(item) !== null && isJunkTracking(sessionStorage.getItem(item))) {
        sessionStorage.removeItem(item);
      }
    } catch (e) {}
  });

  // Always try to capture UTM/ad tracking params from URL
  AdsKeys.forEach(item => {
    const value = searchParams.get(item);
    if (!isJunkTracking(value)) {
      localStorage.setItem(item, value);
    }
  })

  // Cross-domain handoff: cabanapools.com sets cookies on .cabanapools.com
  // when ad traffic lands there first. Read those cookies as a fallback so
  // visitors who explored the marketing site before clicking a quote CTA
  // still get the promo / utm signal applied here.
  const readRootCookie = (name) => {
    const m = document.cookie.match(new RegExp('(?:^|;\\s*)' + name + '=([^;]*)'));
    return m ? decodeURIComponent(m[1]) : null;
  };
  AdsKeys.forEach(item => {
    if (!localStorage.getItem(item)) {
      const fromCookie = readRootCookie(item);
      if (!isJunkTracking(fromCookie)) localStorage.setItem(item, fromCookie);
    }
  })

  // OpenAI Ads click id: the oaiq pixel stores it in the __oppref cookie on
  // .cabanapools.com (cross-subdomain), not a plain 'oppref' cookie, so the
  // loop above misses it. Bridge __oppref into the oppref key.
  if (!localStorage.getItem('oppref')) {
    const fromOpenAi = readRootCookie('__oppref');
    if (!isJunkTracking(fromOpenAi)) localStorage.setItem('oppref', fromOpenAi);
  }

  // Auto-derive utm_source/utm_medium from click IDs when not explicitly set.
  // Google Ads auto-tagging only adds gclid to the URL without UTM params,
  // so 94% of Google Ads leads were missing utm_source/utm_medium attribution.
  if (!localStorage.getItem('utm_source')) {
    if (localStorage.getItem('gclid') || localStorage.getItem('gbraid') || localStorage.getItem('wbraid')) {
      localStorage.setItem('utm_source', 'google');
      localStorage.setItem('utm_medium', 'cpc');
    } else if (localStorage.getItem('fbclid')) {
      localStorage.setItem('utm_source', 'meta');
      localStorage.setItem('utm_medium', 'paid');
    } else if (localStorage.getItem('msclkid')) {
      localStorage.setItem('utm_source', 'bing');
      localStorage.setItem('utm_medium', 'cpc');
    }
  }

  // Auto-derive promo from retargeting campaign attribution.
  // Every Meta retargeting ad URL carries utm_campaign=retargeting-180d or
  // retargeting-14d, and those creatives all promise $100 off the first
  // month. Stamp the promo flag so the quote page can show it and Sell can
  // record it for sales to apply on first invoice.
  if (!localStorage.getItem('promo')) {
    const utmCampaign = localStorage.getItem('utm_campaign') || '';
    if (utmCampaign.startsWith('retargeting')) {
      localStorage.setItem('promo', '100OFF1');
    }
  }

  // Capture document.referrer as 'source' if not already set
  // Only set on first page load (when no source exists)
  // Skip internal referrers from cabanapools.com and its subdomains
  if (!localStorage.getItem('source') && document.referrer) {
    try {
      const referrerHost = new URL(document.referrer).hostname.toLowerCase();
      // The shared deployment serves every brand quote host. Treat that
      // brand's own marketing domain as internal so Pool Logic, Rancho, and
      // Cody visits are not mislabeled as referral traffic.
      const brandDomain = cabanaConfig.siteOrigin.toLowerCase();
      const isInternalReferrer = referrerHost === brandDomain ||
                                  referrerHost.endsWith('.' + brandDomain);
      if (!isInternalReferrer) {
        localStorage.setItem('source', document.referrer);
      }
    } catch (e) {
      // If URL parsing fails, still save the referrer
      localStorage.setItem('source', document.referrer);
    }
  }

  // First-touch landing page. The quote app runs on quote.<brand>.com, so the
  // page that actually earned the visit is the marketing-site page that linked
  // here (url_referrer param stamped by the WP CTA links) when present, else
  // this app's own entry URL (direct ad landings keep their query string,
  // which downstream attribution can parse). First-touch: never overwritten.
  if (!localStorage.getItem('landing_page')) {
    const lp = searchParams.get('url_referrer') || window.location.href;
    if (lp) {
      localStorage.setItem('landing_page', lp);
    }
  }

  // Capture GA4 client ID and session ID early
  // These need to be available for both hero entry and step 1 entry points
  const captureGA4Data = () => {
    // Capture GA4 client ID from dataLayer or cookie
    if (!localStorage.getItem('ga4_id') || !window.ga4_id) {
      const ga4Id = (window.dataLayer?.find(o => o.user_pseudo_id) || {}).user_pseudo_id ||
        document.cookie.match(/(?:^|;\s*)_ga=GA1\.\d\.(\d+\.\d+)/)?.[1] || '';
      if (ga4Id) {
        localStorage.setItem('ga4_id', ga4Id);
        window.ga4_id = ga4Id;
      }
    }

    // Capture GA session ID via gtag (async)
    if (!localStorage.getItem('ga_session_id') && typeof window.gtag !== 'undefined') {
      window.gtag('get', cabanaConfig.measurementId, 'session_id', (sessionId) => {
        if (sessionId) {
          localStorage.setItem('ga_session_id', sessionId);
        }
      });
    }
  };

  // Try to capture GA4 data immediately
  captureGA4Data();

  // Also retry after a short delay in case gtag loads late
  setTimeout(captureGA4Data, 1000);
  setTimeout(captureGA4Data, 3000);

  if(searchParams.get('googlePlace')){
    const address = searchParams.get('address');
    const name = searchParams.get('name');
    const phone = searchParams.get('phone');
    const googlePlace = searchParams.get('googlePlace');
    const googlePlaceId = searchParams.get('googlePlaceId');
    const url_referrer = searchParams.get('url_referrer');

    if (address) {
      localStorage.setItem('address', address);
    }

    if (name) {
      localStorage.setItem('name', name);
    }

    if (phone) {
      localStorage.setItem('phone', phone);
    }

    if (googlePlace !== null) {
      localStorage.setItem('googlePlace', googlePlace === 'true');
    }

    if (googlePlaceId) {
      localStorage.setItem('googlePlaceId', googlePlaceId);
    }

    if (url_referrer) {
      localStorage.setItem('url_referrer', url_referrer);
    }
    console.log('googlePlaceId', localStorage);
  }

  // Pre-seed Statsig localStorage with shared stable ID before SDK init
  const sharedStableId = getAndSeedSharedStableId();

  return (
    <StatsigProvider
      sdkKey={STATSIG_CLIENT_KEY}
      waitForInitialization={true}
      user={getStatsigUser()}
      options={{
        environment: { tier: process.env.NODE_ENV === 'production' ? 'production' : 'development' },
        ...(sharedStableId ? { overrideStableID: sharedStableId } : {})
      }}
    >
      <ToastContainer position="top-right" autoClose={3000} />
      <AcquisitionPopup popup={cabanaConfig.acquisitionPopup} brandKey={cabanaConfig.key} />
      <div id="main-wrapper">
      <BrowserRouter>
        <AppRoutes />
        </BrowserRouter>
        </div>
    </StatsigProvider>
  );
}

export default App;
